import { UseQueryOptions, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import dayjs from "dayjs";

import {
  MoneyFlowRequestDto,
  PlayerTimingsByDurationResponseDto,
  PlayerTimingsResponseDto,
  PlayersResultsStatisticResponseDto,
} from "@/shared/api";
import { useApi } from "@/shared/api/use-api";

export const prepareMoneyFlowRequestDto = (data: MoneyFlowRequestDto) => {
  const result = structuredClone(data);

  if (result?.game_criteria?.timestamp_period) {
    result.game_criteria.timestamp_period.gte = dayjs
      .utc(
        dayjs(result.game_criteria.timestamp_period.gte).format("DD.MM.YYYY HH:mm"),
        "DD.MM.YYYY HH:mm"
      )
      .valueOf();

    result.game_criteria.timestamp_period.lte = dayjs
      .utc(
        dayjs(result.game_criteria.timestamp_period.lte).format("DD.MM.YYYY HH:mm"),
        "DD.MM.YYYY HH:mm"
      )
      .valueOf();
  }

  return result;
};

export const useStatAnalyticsGetMoneyFlowQuery = ({
  domain,
  moneyFlowRequestDto,
  vsHero = false,
  queryOptions,
}: {
  domain: string;
  moneyFlowRequestDto: MoneyFlowRequestDto;
  vsHero?: boolean;
  queryOptions?: Omit<
    UseQueryOptions<AxiosResponse<PlayersResultsStatisticResponseDto>>,
    "queryKey"
  >;
}) => {
  const { statAnalyticsApi } = useApi();
  return useQuery({
    queryKey: [`/stat-analytics/${domain}/money-flow`, moneyFlowRequestDto],
    queryFn: ({ signal }) => {
      return statAnalyticsApi.getPlayersResultsStatistic(
        prepareMoneyFlowRequestDto(moneyFlowRequestDto),
        vsHero,
        {
          signal,
        }
      );
    },
    ...queryOptions,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useStatAnalyticsPlayersTimingStatistics = ({
  domain,
  moneyFlowRequestDto,
  queryOptions,
}: {
  domain: string;
  moneyFlowRequestDto: MoneyFlowRequestDto;
  queryOptions?: Omit<UseQueryOptions<AxiosResponse<PlayerTimingsResponseDto>>, "queryKey">;
}) => {
  const { statAnalyticsApi } = useApi();
  return useQuery({
    queryKey: [`/stat-analytics/${domain}/player-timings`],
    queryFn: ({ signal }) => {
      return statAnalyticsApi.getPlayersTimingsStatistic(
        prepareMoneyFlowRequestDto(moneyFlowRequestDto),
        { signal }
      );
    },
    ...queryOptions,
  });
};

export const useStatAnalyticsPlayersTimingStatisticsByDuration = ({
  domain,
  moneyFlowRequestDto,
  queryOptions,
}: {
  domain: string;
  moneyFlowRequestDto: MoneyFlowRequestDto;
  queryOptions?: Omit<
    UseQueryOptions<AxiosResponse<PlayerTimingsByDurationResponseDto>>,
    "queryKey"
  >;
}) => {
  const { statAnalyticsApi } = useApi();
  return useQuery({
    queryKey: [`/stat-analytics/${domain}/players-timings-by-duration`],
    queryFn: ({ signal }) => {
      return statAnalyticsApi.getPlayersTimingsByDurationStatistic(
        prepareMoneyFlowRequestDto(moneyFlowRequestDto),
        {
          signal,
        }
      );
    },
    ...queryOptions,
  });
};

export const useStatDiversityPageQuery = (filters: MoneyFlowRequestDto | null) => {
  const { statAnalyticsApi } = useApi();

  return useQuery({
    queryKey: [`/stat-analytics/stat-diversity`, filters],
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!filters,
    queryFn: ({ signal }) => {
      // @ts-expect-error valid
      return statAnalyticsApi.getPlayerStatsAnalytic(prepareMoneyFlowRequestDto(filters), {
        signal,
      });
    },
  });
};
